import React from "react";
import { useLocation } from "react-router-dom";
import { useAuth, useEquipment, useLeave, useTraining } from "../api";
import { usePermissions, Permission } from "./permissions";
import type { AlertItem } from "../../api/zekay/alerts";
import {
  generateHRAlerts,
  generateInventoryAlerts,
  generateTrainingAlerts,
  generateUserDocumentAlerts,
} from "./bellAlertsGenerators";

export type BellAlertRouteConfig = Record<string, { enabled: boolean; categories: string[] }>;

type BellAlertDisplayContextType = {
  getForPath: (pathname: string) => { enabled: boolean; categories: string[] };
};

const BellAlertDisplayContext = React.createContext<BellAlertDisplayContextType | undefined>(
  undefined,
);

export function BellAlertDisplayProvider({
  config,
  children,
}: {
  config: BellAlertRouteConfig;
  children: React.ReactNode;
}) {
  const getForPath = React.useCallback(
    (pathname: string) => {
      const top = (pathname || "/").split("/")[1] || "";
      return config[top] || { enabled: false, categories: [] };
    },
    [config],
  );
  const value = React.useMemo(() => ({ getForPath }), [getForPath]);
  return (
    <BellAlertDisplayContext.Provider value={value}>{children}</BellAlertDisplayContext.Provider>
  );
}

export function useBellAlertDisplay() {
  const ctx = React.useContext(BellAlertDisplayContext);
  if (!ctx) throw new Error("useBellAlertDisplay must be used within BellAlertDisplayProvider");
  return ctx;
}

interface BellAlertsContextType {
  alerts: AlertItem[];
  setAlerts: React.Dispatch<React.SetStateAction<AlertItem[]>>;
  open: boolean;
  setOpen: (open: boolean) => void;
  refresh: () => Promise<void>;
  resetErrorState: () => void;
  isError: boolean;
  errorCount: number;
  isLoading: boolean;
}

const BellAlertsContext = React.createContext<BellAlertsContextType | undefined>(undefined);

export function BellAlertsProvider({ children }: { children: React.ReactNode }) {
  const [alerts, setAlerts] = React.useState<AlertItem[]>([]);
  const [open, setOpen] = React.useState(false);
  const [isError, setIsError] = React.useState(false);
  const [errorCount, setErrorCount] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(false);
  const inFlightRef = React.useRef(false);
  const requestIdRef = React.useRef(0);
  const refreshRef = React.useRef<(() => Promise<void>) | null>(null);
  const { isLogged, user, getUsers, getUserDocumentsAlertBatch } = useAuth();
  const currentCompanyId =
    user?.Role?.company_id ??
    user?.company_id ??
    (user?.Role as { CompanyId?: number } | undefined)?.CompanyId;
  const { hasPermission } = usePermissions();
  const { getEquipmentModels, searchEquipmentsBatch } = useEquipment();
  const { getLeaveRequests } = useLeave();
  const { getTrainingAlerts } = useTraining();
  const location = useLocation();
  const { getForPath } = useBellAlertDisplay();

  React.useEffect(() => setOpen(false), [location?.pathname]);

  const hasInventoryPermission = React.useMemo(
    () => hasPermission(Permission.EQUIPMENT_VIEW),
    [hasPermission],
  );
  const hasLeavePermission = React.useMemo(
    () => hasPermission(Permission.LEAVE_ACCESS) && hasPermission(Permission.LEAVE_VALIDATE),
    [hasPermission],
  );
  const hasUserDocumentsAlertPermission = React.useMemo(
    () =>
      hasPermission(Permission.USERS_ACCESS) &&
      (hasPermission(Permission.USERS_ALL_VIEW) || hasPermission(Permission.USERS_COMPANY_VIEW)) &&
      hasPermission(Permission.USERS_DOCUMENTS_VIEW),
    [hasPermission],
  );
  const hasTrainingAlertPermission = React.useMemo(
    () => hasPermission(Permission.TRAININGS_VIEW),
    [hasPermission],
  );

  const routeAlertsEnabled = React.useMemo(() => {
    return getForPath(location?.pathname || "/").enabled;
  }, [getForPath, location?.pathname]);

  const refresh = React.useCallback(async () => {
    if (!isLogged || inFlightRef.current || !routeAlertsEnabled) return;
    const wantInventory = hasInventoryPermission;
    const wantHR = hasLeavePermission;
    const wantUserDocs = hasUserDocumentsAlertPermission;
    const wantTrainings = hasTrainingAlertPermission;
    if (!wantInventory && !wantHR && !wantUserDocs && !wantTrainings) return;

    const requestId = (requestIdRef.current += 1);
    inFlightRef.current = true;
    setIsLoading(true);

    try {
      const tasks: Array<Promise<AlertItem[]>> = [];
      if (wantInventory) {
        tasks.push(
          generateInventoryAlerts(
            { getEquipmentModels, searchEquipmentsBatch },
            {
              ShouldContinue: () => {
                if (requestId !== requestIdRef.current) return false;
                return getForPath(window.location?.pathname || "/").enabled;
              },
            },
          ),
        );
      }
      if (wantHR) tasks.push(generateHRAlerts({ getLeaveRequests }));
      if (wantTrainings) tasks.push(generateTrainingAlerts({ getTrainingAlerts }));
      if (wantUserDocs) {
        tasks.push(
          generateUserDocumentAlerts(
            { getUsers, getUserDocumentsAlertBatch },
            {
              ShouldContinue: () => {
                if (requestId !== requestIdRef.current) return false;
                return getForPath(window.location?.pathname || "/").enabled;
              },
            },
          ),
        );
      }

      const chunks = await Promise.all(tasks);
      const combinedAlerts: AlertItem[] = chunks.flat();

      if (requestId !== requestIdRef.current) return;
      setAlerts(combinedAlerts);
      setIsError(false);
      setErrorCount(0);
    } catch {
      if (requestId !== requestIdRef.current) return;
      setIsError(true);
      setErrorCount((prev) => prev + 1);
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false);
      inFlightRef.current = false;
    }
  }, [
    getEquipmentModels,
    searchEquipmentsBatch,
    getLeaveRequests,
    getTrainingAlerts,
    getUsers,
    getUserDocumentsAlertBatch,
    hasInventoryPermission,
    hasLeavePermission,
    hasUserDocumentsAlertPermission,
    hasTrainingAlertPermission,
    getForPath,
    routeAlertsEnabled,
    isLogged,
  ]);

  React.useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh]);

  React.useEffect(() => {
    if (isLogged && routeAlertsEnabled) void refreshRef.current?.();
  }, [isLogged, location?.pathname, routeAlertsEnabled, currentCompanyId]);

  React.useEffect(() => {
    let intervalId: NodeJS.Timeout | undefined;
    const focusHandler = () => void refreshRef.current?.();
    if (isLogged) {
      void refreshRef.current?.();
      intervalId = setInterval(() => void refreshRef.current?.(), 300000);
      window.addEventListener("focus", focusHandler);
    } else {
      setAlerts([]);
      setOpen(false);
      setIsError(false);
      setErrorCount(0);
      requestIdRef.current += 1;
      inFlightRef.current = false;
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
      window.removeEventListener("focus", focusHandler);
    };
  }, [isLogged]);

  const resetErrorState = React.useCallback(() => {
    setIsError(false);
    setErrorCount(0);
  }, []);

  const value = React.useMemo(
    () => ({
      alerts,
      setAlerts,
      open,
      setOpen,
      refresh,
      resetErrorState,
      isError,
      errorCount,
      isLoading,
    }),
    [alerts, open, refresh, resetErrorState, isError, errorCount, isLoading],
  );

  return <BellAlertsContext.Provider value={value}>{children}</BellAlertsContext.Provider>;
}

export function useBellAlerts() {
  const ctx = React.useContext(BellAlertsContext);
  if (!ctx) throw new Error("useBellAlerts must be used within BellAlertsProvider");
  return ctx;
}
